import { useGetSettingsBasicPublic } from '@agos/api-client/platform'
import {
  LANGUAGE_TO_COUNTRY,
  offeredLanguages,
  getCurrentLanguage,
  langInfo,
  setLanguage,
  tt,
} from '@agos/shared'
import { Check, Globe } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

/**
 * The language the customer reads the site in.
 *
 * The catalogue and the runtime are `@agos/shared`'s, so the customer app and the manager pick from the
 * same list and write the same `fk_lang` key — a household where one person runs the till and another
 * orders dinner has one preference on that browser, not two.
 *
 * **Only the shipped locales are offered.** The catalogue lists twelve languages; the ones with bundles
 * are what `SHIPPED_LOCALES` names. Legacy offers whatever `autoTranslateLanguages` lists in settings,
 * which is a different list — a franchise that enables Spanish there gets an app that falls back to
 * Slovak, and reads as the switcher being broken.
 *
 * The language is **chosen, never inferred from the browser** — that is the manager's rule and it holds
 * here for the same reason: a Slovak restaurant's customer with an English phone still wants the menu
 * they can read at the door.
 *
 * The design is legacy's: a flag chip that opens a list, not a `<select>`. The flags are the same
 * self-hosted public-domain SVGs, because Windows renders the regional-indicator emoji as bare letters —
 * "SK SK" — which is the reason legacy stopped using them.
 */


function Flag({ code, size = 14 }: { code: string; size?: number }) {
  const iso = LANGUAGE_TO_COUNTRY[code] ?? code
  return (
    <img
      src={`/flags/${iso}.svg`}
      alt={code.toUpperCase()}
      width={Math.round(size * 1.4)}
      height={size}
      loading="lazy"
      style={{ display: 'inline-block', verticalAlign: 'middle', objectFit: 'cover', borderRadius: 2 }}
    />
  )
}

export function LanguageSwitcher({ variant = 'header' }: { variant?: 'header' | 'footer' }) {
  const [current, setCurrent] = useState(getCurrentLanguage)
  // §9.92 — only the languages the franchise turned on in *AI preklad*. `basic-public` was built to
  // answer exactly this and nothing read it; `offeredLanguages` keeps the default and falls back to
  // every shipped bundle when the call has not answered, so a slow read never shrinks the menu.
  const { data: languageSettings } = useGetSettingsBasicPublic()
  const offered = offeredLanguages(
    (languageSettings as { autoTranslateLanguages?: string[] } | undefined)?.autoTranslateLanguages,
    (languageSettings as { defaultLanguage?: string } | undefined)?.defaultLanguage,
  )

  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    function onDown(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const info = langInfo(current)
  const button =
    variant === 'header'
      ? 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-secondary transition-colors'
      : 'flex items-center gap-1.5 px-2 py-1 text-xs rounded hover:bg-muted/30 transition-colors'

  return (
    <div className="relative" ref={box} data-language-switcher>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={button}
        aria-label={tt('Jazyk')}
        aria-expanded={open}
        title={tt('Zmeniť jazyk')}
      >
        <Flag code={info.code} size={14} />
        <span className="text-xs font-bold uppercase">{info.code}</span>
        <Globe className="w-3 h-3 opacity-50" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50">
          {offered.map((code) => (
            <button
              key={code}
              type="button"
              data-language={code}
              onClick={() => {
                setLanguage(code)
                setCurrent(code)
                setOpen(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 text-foreground text-sm"
            >
              <Flag code={code} size={16} />
              <span className="flex-1">{langInfo(code).label}</span>
              {current === code && <Check className="w-3.5 h-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
